/**
 * Lifecycle events recorded in ApplicationPrivacyConsentEventLog for each auto-generated privacy
 * consent form (envelope). 'signed' corresponds to the ApplicationPrivacyConsent 'accepted' status;
 * 'dispatched'/'dispatch_failed' capture the DocuSign send outcome (the status stays 'created').
 * 'opened' is reserved for a future DocuSign recipient-delivered signal and is currently only
 * present on backfilled rows.
 */
export type ConsentEventType =
  | 'created'
  | 'dispatched'
  | 'dispatch_failed'
  | 'opened'
  | 'signed'
  | 'declined'
  | 'expired'
  | 'superseded'
  | 'invalidated'
  // An admin manually completed consent without a customer signature.
  | 'bypassed';

export type ConsentDashboardGranularity = 'day' | 'week' | 'month';

export const CONSENT_DASHBOARD_GRANULARITIES: ConsentDashboardGranularity[] = ['day', 'week', 'month'];

/**
 * A single journey outcome per individual (not per form). 'signed' wins if the individual ever
 * signed any of their forms; otherwise it reflects the status of their latest form (pending covers
 * created/opened; 'invalidated' also absorbs any residual superseded-as-latest edge case).
 */
export type ConsentIndividualOutcome = 'signed' | 'pending' | 'declined' | 'expired' | 'invalidated';

/**
 * Range that scopes every figure on the dashboard (and the CSV export). The dashboard is INDIVIDUAL
 * grain: from/to are ISO dates applied to each individual's FIRST consent form creation time - the
 * cohort of individuals who entered the consent process in the window. "Current" is a range ending
 * today; a "historical point-in-time" view is any past range.
 */
export interface ConsentDashboardQuery {
  from: string; // ISO date, inclusive
  to: string;   // ISO date, inclusive (treated as end-of-day server-side)
  granularity: ConsentDashboardGranularity;
}

export interface ConsentDashboardMetrics {
  // Individual grain: an individual = one ApplicationIndividual, across all their (resent/reissued)
  // forms. Counts are mutually exclusive and sum to totalIndividuals via the outcome breakdown.
  totalIndividuals: number;
  signedIndividuals: number;
  pendingIndividuals: number;
  declinedIndividuals: number;
  expiredIndividuals: number;
  // signedIndividuals / totalIndividuals * 100
  completionRatePercent: number;
  // Individual journey: first form created -> last form signed.
  avgHoursToSign: number | null;
  medianHoursToSign: number | null;
  avgHoursToOpen: number | null;
  // Envelope grain: total forms sent to the cohort's individuals, and the average time for a single
  // form to reach any terminal outcome (created -> first of signed/declined/expired/superseded/invalidated).
  totalEnvelopes: number;
  avgFormsPerIndividual: number;
  avgHoursToActionEnvelope: number | null;
}

// One bucket (day/week/month) of the trend chart: individuals who entered vs how many of them ever
// signed, and the point-in-time average time-to-sign for individuals who entered in that bucket.
export interface ConsentDashboardTimeseriesPoint {
  bucketStart: string; // ISO date
  entered: number;
  signed: number;
  avgHoursToSign: number | null;
}

export interface ConsentDashboardStatusBreakdownItem {
  outcome: ConsentIndividualOutcome;
  count: number;
}

export interface ConsentDashboardAnalytics {
  query: ConsentDashboardQuery;
  metrics: ConsentDashboardMetrics;
  timeseries: ConsentDashboardTimeseriesPoint[];
  statusBreakdown: ConsentDashboardStatusBreakdownItem[];
}
